import { useCallback, useEffect, useRef, useState } from 'react';
import Layout from './Layout';
import {
    Box, CircularProgress, IconButton, InputAdornment, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContractService from '../services/ContractService';
import QuickViewDrawer from './sub-components/QuickViewDrawer';
import PageHeader from './PageHeader';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useTranslation } from 'react-i18next';

const ACCENT = '#427898';

const STATUSES = [
    { code: 'DRAFT',             color: '#94a3b8' },
    { code: 'SIGNING',           color: '#F4A261' },
    { code: 'CHANGES_REQUESTED', color: '#E07A5F' },
    { code: 'ACTIVE',            color: '#2EC4B6' },
    { code: 'TERMINATED',        color: '#B0B0B0' },
    { code: 'CANCELLED',         color: '#B0B0B0' },
];
const statusColor = (code) => STATUSES.find(s => s.code === code)?.color ?? '#94a3b8';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('de-CH') : '—';
const fmtCHF = (v) => v != null ? `CHF ${Number(v).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

function FilterPill({ label, count, active, onClick, color }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                px: 1.5, py: 0.5, borderRadius: '20px', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 0.75,
                fontSize: 13, fontWeight: active ? 700 : 500,
                bgcolor: active ? (color || ACCENT) : 'transparent',
                color: active ? '#fff' : '#64748B',
                border: '1.5px solid',
                borderColor: active ? (color || ACCENT) : '#E2E8F0',
                userSelect: 'none', whiteSpace: 'nowrap',
                transition: 'all 0.12s',
                '&:hover': { borderColor: color || ACCENT, color: active ? '#fff' : (color || ACCENT), bgcolor: active ? (color || ACCENT) : `${color || ACCENT}10` },
            }}
        >
            {label}
            {count != null && (
                <Box component="span" sx={{ fontSize: 11, fontWeight: 700, opacity: active ? 0.85 : 0.6 }}>{count}</Box>
            )}
        </Box>
    );
}

function StatusPill({ code, label }) {
    const color = statusColor(code);
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color }}>{label}</Typography>
        </Box>
    );
}

function StatCard({ label, value, sub }) {
    return (
        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, px: 2.5, py: 1.75, minWidth: 170 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#0F172A', mt: 0.5, lineHeight: 1.2 }}>{value}</Typography>
            {sub && <Typography sx={{ fontSize: 12, color: '#94A3B8', mt: 0.25 }}>{sub}</Typography>}
        </Paper>
    );
}

export default function ContractsPage() {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { t } = useTranslation();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [stats, setStats] = useState(null);
    const [quickView, setQuickView] = useState(null);

    const fetchTimer = useRef(null);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const data = await ContractService.getContracts(page, rowsPerPage, search.trim(), selectedStatuses);
            setRows(data.content ?? []);
            setTotal(data.totalElements ?? 0);
        } catch {
            showToast(t('contracts.notFound'), 'error');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, search, selectedStatuses]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        clearTimeout(fetchTimer.current);
        fetchTimer.current = setTimeout(fetchRows, 250);
        return () => clearTimeout(fetchTimer.current);
    }, [fetchRows]);

    useEffect(() => {
        ContractService.getStats().then(setStats).catch(() => {});
    }, []);

    const counts = stats?.countsByStatus ?? {};
    const totalCount = Object.values(counts).reduce((a, b) => a + b, 0);

    const toggleStatus = (code) => {
        setPage(0);
        setSelectedStatuses(prev => prev.includes(code) ? prev.filter(s => s !== code) : [...prev, code]);
    };

    const statusLabel = (code) => t(`contracts.statuses.${code}`, { defaultValue: code });

    return (
        <Layout>
            <Box sx={{ p: 3 }}>
                <PageHeader title={t('contracts.title')} count={totalCount || null} />

                {/* Stats strip */}
                {stats && (
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                        <StatCard
                            label={t('contracts.stats.activeValue', { defaultValue: 'Active value' })}
                            value={fmtCHF(stats.activeValue)}
                            sub={`${counts.ACTIVE ?? 0} ${statusLabel('ACTIVE').toLowerCase()}`}
                        />
                        <StatCard
                            label={t('contracts.stats.inSigning', { defaultValue: 'In signing' })}
                            value={(counts.SIGNING ?? 0) + (counts.CHANGES_REQUESTED ?? 0)}
                        />
                        <StatCard
                            label={t('contracts.stats.drafts', { defaultValue: 'Drafts' })}
                            value={counts.DRAFT ?? 0}
                        />
                    </Box>
                )}

                {/* Filter bar */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: '12px', px: 2, py: 1.25, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'auto', flex: 1, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                        <FilterPill
                            label={t('common.all', { defaultValue: 'All' })}
                            active={selectedStatuses.length === 0}
                            onClick={() => { setSelectedStatuses([]); setPage(0); }}
                        />
                        {STATUSES.map(s => (
                            <FilterPill
                                key={s.code}
                                label={statusLabel(s.code)}
                                count={counts[s.code] ?? 0}
                                active={selectedStatuses.includes(s.code)}
                                onClick={() => toggleStatus(s.code)}
                                color={s.color}
                            />
                        ))}
                    </Box>
                    <TextField
                        size="small"
                        placeholder={t('contracts.search')}
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#aaa', fontSize: 20 }} /></InputAdornment>
                        }}
                        sx={{ width: 240, flexShrink: 0, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                </Box>

                {/* Table */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                        <CircularProgress sx={{ color: ACCENT }} />
                    </Box>
                ) : rows.length === 0 ? (
                    <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '12px', bgcolor: '#fff', py: 8, textAlign: 'center' }}>
                        <GavelIcon sx={{ fontSize: 40, color: '#CBD5E1', mb: 1.5 }} />
                        <Typography fontWeight={600} color="#475569">{t('contracts.empty')}</Typography>
                    </Box>
                ) : (
                    <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '12px', overflow: 'hidden' }}>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                        {[t('contracts.table.number'), t('contracts.table.customer'), t('contracts.table.quote'), t('contracts.table.status'), t('contracts.table.value'), t('contracts.table.created'), ''].map((h, i) => (
                                            <TableCell
                                                key={i}
                                                align={i === 4 ? 'right' : 'left'}
                                                sx={{ fontWeight: 700, fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.6, py: 1.5, ...(i === 6 ? { width: 80, p: 0 } : {}) }}
                                            >
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map(c => (
                                        <TableRow
                                            key={c.id}
                                            onClick={() => navigate(`/contracts/${c.id}`)}
                                            sx={{
                                                cursor: 'pointer',
                                                '&:last-child td': { border: 0 },
                                                '&:hover': { bgcolor: '#F8FAFC' },
                                                '&:hover .row-actions': { opacity: 1 },
                                                '& .row-actions': { opacity: 0, transition: 'opacity 0.15s' },
                                            }}
                                        >
                                            <TableCell sx={{ py: 1.25 }}>
                                                <Typography sx={{ fontWeight: 700, color: '#0F172A', fontSize: 13 }}>{c.displayName}</Typography>
                                                {c.title && (
                                                    <Typography noWrap sx={{ fontSize: 12, color: '#94A3B8', maxWidth: 260 }}>{c.title}</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13, color: '#475569' }}>{c.customerName ?? '—'}</TableCell>
                                            <TableCell sx={{ fontSize: 13, color: '#475569' }}>{c.quoteDisplayName ?? '—'}</TableCell>
                                            <TableCell><StatusPill code={c.status} label={statusLabel(c.status)} /></TableCell>
                                            <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{fmtCHF(c.totalValue)}</TableCell>
                                            <TableCell sx={{ fontSize: 13, color: '#64748B' }}>{fmtDate(c.createdAt)}</TableCell>
                                            <TableCell sx={{ p: 0, pr: 1 }}>
                                                <Box className="row-actions" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.25 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e) => { e.stopPropagation(); setQuickView(c); }}
                                                        sx={{ color: '#94A3B8', '&:hover': { color: ACCENT, bgcolor: '#F0F7FF' } }}
                                                    >
                                                        <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
                                                    </IconButton>
                                                    <ChevronRightIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                            rowsPerPageOptions={[10, 25, 50]}
                            sx={{ borderTop: '1px solid #F1F5F9' }}
                        />
                    </Paper>
                )}

                {/* Quick view */}
                <QuickViewDrawer
                    open={Boolean(quickView)}
                    onClose={() => setQuickView(null)}
                    title={quickView?.displayName}
                    statusColor={statusColor(quickView?.status)}
                    statusLabel={quickView ? statusLabel(quickView.status) : null}
                    facts={quickView ? [
                        { label: t('contracts.table.customer'), value: quickView.customerName },
                        { label: t('contracts.table.value'), value: fmtCHF(quickView.totalValue) },
                        { label: t('contracts.table.quote'), value: quickView.quoteDisplayName },
                        { label: t('contracts.table.created'), value: fmtDate(quickView.createdAt) },
                    ] : []}
                    loadPdf={() => ContractService.downloadContract(quickView.id)}
                    onOpenFull={() => navigate(`/contracts/${quickView.id}`)}
                    openLabel={t('contracts.quickView.open', { defaultValue: 'Open contract' })}
                    pdfErrorLabel={t('common.pdfPreviewUnavailable', { defaultValue: 'PDF preview not available' })}
                />
            </Box>
        </Layout>
    );
}
